import { AxiomReportType } from '@/enums/axiom-report.enum'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import { transformForChartData } from '@/utils/scenarioAnalysisReportUtils/scenarioAnalysisReportUtils'
import { formatCellValue } from '@/utils/tableGridUtils'
import { escapeSpecialCharacters, formatNumber } from '@/utils/utils'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useRef, useState } from 'react'

export const ScenarioAnalysisOverviewChart: React.FC<{
  id: string
  isFocused: boolean
  defaultChartHeight: string | number
  focusedChartHeight: string | number
  data: any[]
  dataPoint: string
  displayBenchmarkData: boolean
  showLegend?: boolean
}> = ({
  id,
  isFocused,
  defaultChartHeight,
  focusedChartHeight,
  data,
  dataPoint,
  displayBenchmarkData,
  showLegend,
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null)
  const chartData = transformForChartData(dataPoint, data)
  const [chartOptions, setChartOptions] = useState({})

  const defaultChartOptions = {
    chart: {
      type: 'column',
      height: defaultChartHeight,
      style: {
        fontFamily: '"Source Sans 3", sans-serif',
        pading: 0,
      },
    },
    title: {
      text: '',
      style: {
        display: 'none',
      },
    },
    xAxis: {
      categories: chartData?.categories,
      labels: {
        useHTML: true,
        formatter: function (this: any): string {
          let label = this.value
          label = escapeSpecialCharacters(String(label)) // Escape special characters explicitly
          return isFocused ? label : ''
        },
      },
    },
    yAxis: {
      title: {
        text: '',
      },
    },
    tooltip: {
      useHTML: true,
      headerFormat: '',
      pointFormatter: function (this: any): string {
        const formattedValue = formatCellValue(
          formatNumber(this.y),
          getColumnDetail(chartData?.returnType as string, AxiomReportType.SCEN)
            .format,
        )
        const category = escapeSpecialCharacters(this.category) // Escape special characters explicitly
        return `
              <span style="color:${this.color}">${category}</span><br/>
              <span style="color:${this.color}">${this.series.name}</span>: <b>${formattedValue}</b><br>
          `
      },
    },
    series: [
      {
        name: 'Portfolio',
        data: chartData?.portfolio,
        color: '#0A5EB2',
      },
    ],
    legend: {
      enabled: showLegend,
    },
    credits: {
      enabled: false,
    },
  }

  useEffect(() => {
    if (displayBenchmarkData) {
      const benchmarkChartOptions = { ...defaultChartOptions }
      benchmarkChartOptions.series.push({
        name: 'Benchmark',
        data: chartData?.benchmark,
        color: '#00B2BA',
      })
      benchmarkChartOptions.chart.height = isFocused
        ? focusedChartHeight
        : defaultChartHeight
      setChartOptions(benchmarkChartOptions)
    } else {
      const portfolioChartOptions = { ...defaultChartOptions }
      portfolioChartOptions.chart.height = isFocused
        ? focusedChartHeight
        : defaultChartHeight
      setChartOptions(portfolioChartOptions)
    }
  }, [displayBenchmarkData, data, isFocused])

  useEffect(() => {
    const newChartHeight = isFocused ? focusedChartHeight : defaultChartHeight
    const chart = chartRef.current?.chart

    if (chart) {
      chart.update({
        chart: {
          height: newChartHeight,
        },
      })
    }
  }, [isFocused])

  return (
    <HighchartsReact
      highcharts={Highcharts}
      ref={chartRef}
      options={chartOptions}
    />
  )
}
